// AstriaIndiaV2Shell.tsx
import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Heart,
  Clock,
  Compass,
  Smile,
  UserCircle,
  Waves,
  NotebookPen,
} from "lucide-react";
import { IndiaV2Language } from "../../../utils/India/astriaIndiaV2Language";
import { ASTRIA_INDIA_V2_COPY } from "../../../utils/India/astriaIndiaV2Copy";
import { useIndiaV2Copy } from "../../../utils/India/useIndiaV2Copy";

// ─── Astria India v2 — shared shell (header + sticky tab bar)
// ──────────────────────────────────────────────────────────────────────
// Premium "Emotional Guidance OS" design system for the India lane.
// Deep Royal Purple / Soft Lavender / Warm Gold / Ivory — distinct from
// every other Astria lane's palette. Not an astrology-website look.
//
// Multilingual (EN/HI/TA/MR) — `lang` comes from the user's preferred
// language via useIndiaV2Language(); there is no per-screen toggle.

export const INDIA_V2_HEADING_FONT = "'Poppins', 'Inter', sans-serif";
export const INDIA_V2_BODY_FONT = "'Inter', sans-serif";
// Shared cap for header/tab-bar/body so the panel fills more of the chat
// column on wide screens instead of floating with large side margins.
const INDIA_V2_MAX_WIDTH = 960;

export const SHELL_COLORS_INDIA_V2 = {
  bg: "linear-gradient(160deg, #FFFDFB 0%, #F7F1FB 55%, #FBF4EC 100%)",
  card: "#FFFFFF",
  section: "#F7F2FC",
  cardBorder: "#EAE1F5",
  titleColor: "#2E1F45",
  sectionTitleColor: "#2E1F45",
  bodyColor: "#4A3B5C",
  mutedColor: "#8C7DA0",
  accent: "#6B3FA0",
  accentSoft: "rgba(107,63,160,0.10)",
  accentGlow: "0 0 0 3px rgba(107,63,160,0.16)",
  gold: "#C9A24B",
  goldSoft: "rgba(201,162,75,0.14)",
  rose: "#D98CA0",
  white: "#FFFFFF",
};

export type AstriaIndiaV2Tab =
  | "sambandh"
  | "vivah"
  | "upay"
  | "bhavna"
  | "vyaktitva"
  | "samay"
  | "aapka-note";

interface TabDef {
  key: AstriaIndiaV2Tab;
  // English labels kept here only as identifiers/fallback — the displayed
  // label is sourced from ASTRIA_INDIA_V2_COPY[lang], keyed by `key`.
  label: string;
  sublabel: string;
  icon: React.ReactNode;
  path: string;
  accent: [string, string];
}

const TABS: TabDef[] = [
  {
    key: "sambandh",
    label: "Sambandh Taal Mel",
    sublabel: "Relationship",
    icon: <Heart className="w-3.5 h-3.5" />,
    path: "/astria-india-v2/sambandh",
    accent: ["#6B3FA0", "#D98CA0"],
  },
  {
    key: "vivah",
    label: "Vivah Muhurat",
    sublabel: "Timing",
    icon: <Clock className="w-3.5 h-3.5" />,
    path: "/astria-india-v2/vivah",
    accent: ["#C9A24B", "#6B3FA0"],
  },
  {
    key: "upay",
    label: "Upay Marg",
    sublabel: "Guidance",
    icon: <Compass className="w-3.5 h-3.5" />,
    path: "/astria-india-v2/upay",
    accent: ["#2E8B8B", "#6B3FA0"],
  },
  {
    key: "bhavna",
    label: "Bhavna Drishti",
    sublabel: "Emotion Map",
    icon: <Smile className="w-3.5 h-3.5" />,
    path: "/astria-india-v2/bhavna",
    accent: ["#D98CA0", "#C9A9E8"],
  },
  {
    key: "vyaktitva",
    label: "Vyaktitva Darshan",
    sublabel: "Personality",
    icon: <UserCircle className="w-3.5 h-3.5" />,
    path: "/astria-india-v2/vyaktitva",
    accent: ["#4F6FD9", "#6B3FA0"],
  },
  {
    key: "samay",
    label: "Samay Pravah",
    sublabel: "Flow",
    icon: <Waves className="w-3.5 h-3.5" />,
    path: "/astria-india-v2/samay",
    accent: ["#3A4B8C", "#1F2A54"],
  },
  {
    key: "aapka-note",
    label: "Aapka Note",
    sublabel: "Reflection",
    icon: <NotebookPen className="w-3.5 h-3.5" />,
    path: "/astria-india-v2/aapka-note",
    accent: ["#6B3FA0", "#C9A9E8"],
  },
];

// tab key -> copy-file section key, for looking up the translated tabLabel.
const TAB_COPY_KEY: Record<AstriaIndiaV2Tab, keyof typeof ASTRIA_INDIA_V2_COPY["en"]> = {
  sambandh: "sambandh",
  vivah: "vivah",
  upay: "upay",
  bhavna: "bhavna",
  vyaktitva: "vyaktitva",
  samay: "samay",
  "aapka-note": "aapkaNote",
};


interface AstriaIndiaV2ShellProps {
  active: AstriaIndiaV2Tab;
  lang: IndiaV2Language;
  children: React.ReactNode;
  // Read-only signal, never user-switchable from here: which engine
  // ("Astria India V2" vs "Astria India V3") the CURRENT page was reached
  // as — set once by which sidebar entry the user clicked (see
  // Sidebar.tsx/TesterHome.tsx's "?v=3" query flag), never changed inside
  // the page. Only used so the tab bar's own lane-switching navigation
  // (e.g. Vyaktitva -> Samay) carries the same engine forward instead of
  // silently dropping back to V2. Optional so any usage that omits it
  // renders byte-identical to before.
  version?: "v2" | "v3";
}

const AstriaIndiaV2Shell: React.FC<AstriaIndiaV2ShellProps> = ({
  active,
  lang,
  children,
  version,
}) => {
  const navigate = useNavigate();
  const copy = useIndiaV2Copy(lang);

  return (
    <div
      className="min-h-screen"
      style={{
        background: SHELL_COLORS_INDIA_V2.bg,
        fontFamily: INDIA_V2_BODY_FONT,
      }}
    >
      {/* ─── Header ──────────────────────────────────────────────────── */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 20,
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          background: "rgba(255,253,251,0.82)",
          borderBottom: `1px solid ${SHELL_COLORS_INDIA_V2.cardBorder}`,
        }}
      >
        <div
          className="mx-auto flex items-center justify-between gap-3 px-5"
          style={{ maxWidth: INDIA_V2_MAX_WIDTH, paddingTop: 18, paddingBottom: 14 }}
        >
          <div className="flex items-center gap-3" style={{ minWidth: 0 }}>
            <div
              style={{
                width: 38,
                height: 38,
                borderRadius: "50%",
                background: `linear-gradient(135deg, ${SHELL_COLORS_INDIA_V2.accent} 0%, ${SHELL_COLORS_INDIA_V2.gold} 100%)`,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: `0 4px 14px ${SHELL_COLORS_INDIA_V2.accentSoft}`,
                flexShrink: 0,
              }}
            >
              <span style={{ fontSize: 16, color: "#fff" }}>✦</span>
            </div>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: 16,
                  fontWeight: 600,
                  color: SHELL_COLORS_INDIA_V2.titleColor,
                  fontFamily: INDIA_V2_HEADING_FONT,
                  letterSpacing: "0.01em",
                }}
              >
                {copy.shell.title}
              </div>
              <div
                style={{
                  fontSize: 11.5,
                  color: SHELL_COLORS_INDIA_V2.mutedColor,
                }}
              >
                {copy.shell.subtitle}
              </div>
            </div>
          </div>

        </div>

        {/* ─── Tab bar ─────────────────────────────────────────────────
            Tabs stretch (flex: 1) to fill the full row width evenly — no
            leftover space on the right. `flex-wrap` + `minWidth` is the
            fallback for any viewport too narrow to fit all 7 on one line,
            so it wraps to a 2nd row instead of clipping/scrolling. */}
        <div
          className="mx-auto px-5 pb-3"
          style={{
            maxWidth: INDIA_V2_MAX_WIDTH,
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
          }}
        >
          {TABS.map((tab) => {
            const isActive = tab.key === active;
            const tabCopy = copy[TAB_COPY_KEY[tab.key]] as { tabLabel: string };
            return (
              <button
                key={tab.key}
                onClick={() =>
                  navigate(version === "v3" ? `${tab.path}?v=3` : tab.path)
                }
                className="flex items-center justify-center gap-1 rounded-full transition-all whitespace-nowrap"
                style={{
                  flex: "1 1 auto",
                  minWidth: "fit-content",
                  paddingInline: 10,
                  paddingBlock: 7,
                  fontSize: 11,
                  fontWeight: 600,
                  color: isActive ? "#FFFFFF" : SHELL_COLORS_INDIA_V2.bodyColor,
                  background: isActive
                    ? `linear-gradient(135deg, ${tab.accent[0]} 0%, ${tab.accent[1]} 100%)`
                    : SHELL_COLORS_INDIA_V2.section,
                  boxShadow: isActive
                    ? `0 4px 14px ${SHELL_COLORS_INDIA_V2.accentSoft}`
                    : "none",
                  border: isActive
                    ? "none"
                    : `1px solid ${SHELL_COLORS_INDIA_V2.cardBorder}`,
                }}
              >
                {tab.icon}
                <span>{tabCopy.tabLabel}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* ─── Body ────────────────────────────────────────────────────── */}
      <div className="mx-auto px-4 sm:px-5" style={{ maxWidth: INDIA_V2_MAX_WIDTH, paddingTop: 24, paddingBottom: 64 }}>
        {children}
      </div>
    </div>
  );
};

export default AstriaIndiaV2Shell;
export { TABS as ASTRIA_INDIA_V2_TABS };
